/**
 * Receipt Management Service
 * 
 * This service provides functions for managing receipts in the 70Lives Business Finance Tracker.
 */

import { getAuth } from 'firebase/auth';
import {
  addDoc,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const normalizeWorkspaceContext = (workspaceContext = null) => {
  if (!workspaceContext?.id) return null;
  return {
    id: workspaceContext.id,
    ownerId: workspaceContext.ownerId || workspaceContext.id,
    ownerEmail: workspaceContext.ownerEmail || '',
    ownerDisplayName: workspaceContext.ownerDisplayName || ''
  };
};

/**
 * Firestore rejects `undefined` values anywhere in payloads.
 * Remove them recursively before create/update calls.
 */
const stripUndefinedFields = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;

  if (Array.isArray(value)) {
    return value
      .map((entry) => stripUndefinedFields(entry))
      .filter((entry) => entry !== undefined);
  }

  if (typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, entryValue]) => {
      const sanitizedValue = stripUndefinedFields(entryValue);
      if (sanitizedValue !== undefined) {
        acc[key] = sanitizedValue;
      }
      return acc;
    }, {});
  }

  return value;
};

const toTimestampMs = (value) => {
  if (!value) return 0;

  if (typeof value?.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : 0;
  }

  if (typeof value === 'object' && value.seconds !== undefined) {
    const seconds = Number(value.seconds) || 0;
    const nanoseconds = Number(value.nanoseconds) || 0;
    return (seconds * 1000) + Math.round(nanoseconds / 1000000);
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getReceiptEntryTimestamp = (receipt = {}) => {
  const updatedAtTime = toTimestampMs(receipt.updatedAt);
  const createdAtTime = toTimestampMs(receipt.createdAt);
  const processedDateTime = toTimestampMs(receipt.processedDate);

  return Math.max(updatedAtTime, createdAtTime, processedDateTime, 0);
};

const getReceiptBusinessDateTimestamp = (receipt = {}) => {
  const receiptDate = receipt.extractedData?.date || receipt.date;
  const receiptDateTime = toTimestampMs(receiptDate);
  return receiptDateTime > 0 ? receiptDateTime : getReceiptEntryTimestamp(receipt);
};

/**
 * Create a new receipt record
 *
 * @param {Object} receiptData - Receipt payload
 * @returns {Promise<Object>} The created receipt
 */
export const createReceipt = async (receiptData = {}, options = {}) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User not authenticated');
    }

    const workspace = normalizeWorkspaceContext(options.workspace || receiptData.workspace);
    const receiptPayload = stripUndefinedFields({
      ...receiptData,
      userId: user.uid,
      ...(workspace
        ? {
            workspaceId: workspace.id,
            ownerId: workspace.ownerId,
            ownerEmail: workspace.ownerEmail,
            ownerDisplayName: workspace.ownerDisplayName,
            uploadedByUid: user.uid,
            uploadedByEmail: user.email || '',
            uploadedByDisplayName: user.displayName || user.email || ''
          }
        : {}),
      extractedData: receiptData.extractedData || {},
      processedDate: receiptData.processedDate || new Date().toISOString(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    const receiptRef = await addDoc(collection(db, 'receipts'), receiptPayload);
    const createdSnap = await getDoc(receiptRef);

    return {
      id: createdSnap.id,
      ...createdSnap.data()
    };
  } catch (error) {
    console.error('Receipt Management Error:', error);
    throw error;
  }
};

/**
 * Get a list of receipts with optional filtering
 * 
 * @param {Object} filters - Optional filters for the receipts
 * @param {number} limit - Maximum number of receipts to return
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Object>} The receipts and pagination info
 */
export const getReceipts = async (filters = {}, limit = 50, offset = 0, options = {}) => {
  try {
    // Get the current user's ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const workspace = normalizeWorkspaceContext(options.workspace || filters.workspace);
    const receiptsQuery = workspace?.id
      ? query(
          collection(db, 'receipts'),
          where('workspaceId', '==', workspace.id)
        )
      : query(
          collection(db, 'receipts'),
          where('userId', '==', user.uid)
        );

    const snapshot = await getDocs(receiptsQuery);
    let receipts = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    // Apply filters in-memory (avoid Firestore composite index requirements)
    if (filters.vendor) {
      const vendorFilter = filters.vendor.toLowerCase();
      receipts = receipts.filter((receipt) =>
        (receipt.extractedData?.vendor || receipt.vendor || '')
          .toLowerCase()
          .includes(vendorFilter)
      );
    }

    if (filters.category) {
      const categoryFilter = filters.category.toLowerCase();
      receipts = receipts.filter((receipt) =>
        (receipt.extractedData?.category || receipt.category || '')
          .toLowerCase() === categoryFilter
      );
    }

    if (filters.startDate) {
      const start = new Date(filters.startDate).getTime();
      receipts = receipts.filter((receipt) => {
        const dateValue = receipt.extractedData?.date || receipt.date;
        if (!dateValue) return true;
        return new Date(dateValue).getTime() >= start;
      });
    }

    if (filters.endDate) {
      const end = new Date(filters.endDate).getTime();
      receipts = receipts.filter((receipt) => {
        const dateValue = receipt.extractedData?.date || receipt.date;
        if (!dateValue) return true;
        return new Date(dateValue).getTime() <= end;
      });
    }

    const sortBy = String(filters.sortBy || 'latestEntryDesc');

    receipts.sort((a, b) => {
      const aEntryTime = getReceiptEntryTimestamp(a);
      const bEntryTime = getReceiptEntryTimestamp(b);
      const aReceiptDateTime = getReceiptBusinessDateTimestamp(a);
      const bReceiptDateTime = getReceiptBusinessDateTimestamp(b);

      if (sortBy === 'latestEntryAsc') {
        return aEntryTime - bEntryTime;
      }

      if (sortBy === 'receiptDateDesc') {
        return bReceiptDateTime - aReceiptDateTime;
      }

      if (sortBy === 'receiptDateAsc') {
        return aReceiptDateTime - bReceiptDateTime;
      }

      // Default: most recently saved receipt first.
      return bEntryTime - aEntryTime;
    });

    const total = receipts.length;
    const pagedReceipts = receipts.slice(offset, offset + limit);

    return {
      receipts: pagedReceipts,
      total
    };
  } catch (error) {
    console.error('Receipt Management Error:', error);
    throw error;
  }
};

/**
 * Get a single receipt by ID
 * 
 * @param {string} receiptId - The ID of the receipt to get
 * @returns {Promise<Object>} The receipt
 */
export const getReceiptById = async (receiptId, options = {}) => {
  try {
    // Get the current user's ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const receiptRef = doc(db, 'receipts', receiptId);
    const receiptSnap = await getDoc(receiptRef);

    if (!receiptSnap.exists()) {
      throw new Error('Receipt not found');
    }

    const receiptData = receiptSnap.data();

    const workspace = normalizeWorkspaceContext(options.workspace);
    if (
      receiptData.userId &&
      receiptData.userId !== user.uid &&
      (!workspace?.id || receiptData.workspaceId !== workspace.id)
    ) {
      throw new Error('Access denied');
    }

    return {
      id: receiptSnap.id,
      ...receiptData
    };
  } catch (error) {
    console.error('Receipt Management Error:', error);
    throw error;
  }
};

/**
 * Update a receipt
 * 
 * @param {string} receiptId - The ID of the receipt to update
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} The updated receipt
 */
export const updateReceipt = async (receiptId, updateData, options = {}) => {
  try {
    // Get the current user's ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const receiptRef = doc(db, 'receipts', receiptId);
    const receiptSnap = await getDoc(receiptRef);

    if (!receiptSnap.exists()) {
      throw new Error('Receipt not found');
    }

    const receiptData = receiptSnap.data();
    const workspace = normalizeWorkspaceContext(options.workspace);
    if (
      receiptData.userId &&
      receiptData.userId !== user.uid &&
      (!workspace?.id || receiptData.workspaceId !== workspace.id)
    ) {
      throw new Error('Access denied');
    }

    const sanitizedUpdateData = stripUndefinedFields({
      ...(updateData || {}),
      ...(workspace?.id && !receiptData.workspaceId
        ? {
            workspaceId: workspace.id,
            ownerId: workspace.ownerId,
            ownerEmail: workspace.ownerEmail,
            ownerDisplayName: workspace.ownerDisplayName
          }
        : {}),
      updatedByUid: user.uid,
      updatedByEmail: user.email || '',
      updatedByDisplayName: user.displayName || user.email || ''
    });

    await updateDoc(receiptRef, {
      ...sanitizedUpdateData,
      updatedAt: serverTimestamp()
    });

    const updatedSnap = await getDoc(receiptRef);
    return {
      id: updatedSnap.id,
      ...updatedSnap.data()
    };
  } catch (error) {
    console.error('Receipt Management Error:', error);
    throw error;
  }
};

/**
 * Delete a receipt
 * 
 * @param {string} receiptId - The ID of the receipt to delete
 * @returns {Promise<Object>} The deletion confirmation
 */
export const deleteReceipt = async (receiptId, options = {}) => {
  try {
    // Get the current user's ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const receiptRef = doc(db, 'receipts', receiptId);
    const receiptSnap = await getDoc(receiptRef);

    if (!receiptSnap.exists()) {
      throw new Error('Receipt not found');
    }

    const receiptData = receiptSnap.data();
    const workspace = normalizeWorkspaceContext(options.workspace);
    if (
      receiptData.userId &&
      receiptData.userId !== user.uid &&
      (!workspace?.id || receiptData.workspaceId !== workspace.id)
    ) {
      throw new Error('Access denied');
    }

    await deleteDoc(receiptRef);

    return { success: true, message: `Receipt ${receiptId} deleted successfully` };
  } catch (error) {
    console.error('Receipt Management Error:', error);
    throw error;
  }
};

/**
 * Convert a receipt to a transaction
 * 
 * @param {string} receiptId - The ID of the receipt to convert
 * @param {Object} transactionData - Optional transaction data
 * @returns {Promise<Object>} The created transaction
 */
export const convertReceiptToTransaction = async (receiptId, transactionData = {}) => {
  try {
    // Get the current user's ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();
    
    // Make the API request
    const response = await fetch(`/api/v2/70lives-ledger/receipts/${receiptId}/convert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify(transactionData)
    });
    
    // Check for errors
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to convert receipt to transaction');
    }
    
    // Return the created transaction
    return await response.json();
  } catch (error) {
    console.error('Receipt Management Error:', error);
    throw error;
  }
};

// Create a named object before exporting as default
const receiptManagementService = {
  createReceipt,
  getReceipts,
  getReceiptById,
  updateReceipt,
  deleteReceipt,
  convertReceiptToTransaction
};

export default receiptManagementService;
